import * as signalR from "@microsoft/signalr";
import { MessagePackHubProtocol } from "@microsoft/signalr-protocol-msgpack";

export { }; // Ensure this file is treated as a module

// ============================================================================
// TYPE DEFINITIONS & GLOBAL INTERFACES
// ============================================================================

declare global {
  interface Window {
    tsNameFunctionDictionary?: Map<string, { f: Function, target: any }>;
    jsE?: (objectId: number, data: any, delay?: boolean) => void;
    ood?: (id: number, object: any) => any;
    oos?: (id: number) => any;
    skipExitValidation?: boolean;
    afterRender?: Function[];
    restartSignalR?: () => void;
    debugsignalr?: any;
    bootstrapSessionIdURL?: string;
    clsLoad?: (obj: { [x: string]: any }, funcName: string) => void;
    legacyJsLoad?: () => void;
  }
}

// ============================================================================
// CONSTANTS & STATE MANAGEMENT
// ============================================================================

const functionDictionary = new Map();
const functionIdNameMap = new Map<number, string>();
const varObjectWeakMap = new WeakMap();
const objIdIndex = new Map();

// ============================================================================
// PREDEFINED FUNCTIONS (Legacy Support)
// ============================================================================

// Hard-wired window function definitions (TODO: remove and use functionDictionary)
const initializePredefinedFunctions = () => {
  functionDictionary.set(3, {
    f: function (m: any) {
      if (m) window.alert(m);
      window.skipExitValidation = true;
      window.location.reload();
    },
    t: null
  }); // Error handler

  functionDictionary.set(4, {
    f: function (m: any) {
      window.alert(m);
    },
    t: null
  }); // Alert

  functionDictionary.set(7, {
    f: function (funcId: any, name: any) {
      functionIdNameMap.set(funcId, name);
    },
    t: null
  }); // Function registration
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function applyMessage([f, a, s]: [number, any[], number]) {
  if (!functionDictionary.has(f)) {
    let fName = functionIdNameMap.get(f);
    let fRef = window.tsNameFunctionDictionary?.get(fName!);
    functionDictionary.set(f, fRef);
  }
  const fnc = functionDictionary.get(f);
  fnc.f.apply(fnc.target, a);
}

function createOverlay() {
  const existingOverlay = document.getElementById("cnopverposed");
  if (existingOverlay?.parentElement) {
    existingOverlay.parentElement.removeChild(existingOverlay);
  }
  
  const overlay = document.createElement("div");
  overlay.id = "cnopverposed";
  overlay.innerHTML = '<div style="font-family: monospace; font-size:15px; display: flex; justify-content: center; align-items: center; height: 100%;">[ RECONECTANDO ]</div>';
  document.body.insertBefore(overlay, document.body.firstChild);
}

function removeOverlay() {
  const overlay = document.getElementById("cnopverposed");
  if (overlay?.parentElement) {
    overlay.parentElement.removeChild(overlay);
  }
  console.log("Overlay removed");
}

// ============================================================================
// OBJECT MANAGEMENT SYSTEM
// ============================================================================

const initializeObjectManagement = () => {
  // Declares the object and allows it to be routed for events
  window.ood = function (id, object) {
    const numericId = parseInt(id.toString());
    object.setAttribute("id", numericId);
    const holderRef = new Object();
    objIdIndex.set(numericId, holderRef);
    varObjectWeakMap.set(holderRef, object);
    return object;
  };

  // Returns the object by id
  window.oos = function (id: number | string) {
    const numericId = parseInt(id.toString());
    return varObjectWeakMap.get(objIdIndex.get(numericId));
  };
};

// ============================================================================
// CLASS LOADING SYSTEM
// ============================================================================

const initializeClassLoading = () => {
  if (!window.tsNameFunctionDictionary) {
    window.tsNameFunctionDictionary = new Map();
  }

  window.clsLoad = function (obj: { [x: string]: any; }, funcName: string) {
    for (const key in obj) {
      if (typeof obj[key] === "function") {
        window.tsNameFunctionDictionary!.set(funcName + "|" + key, {
          f: obj[key],
          target: obj
        });
      }
    }
  };
};

// ============================================================================
// SIGNALR CONNECTION MANAGEMENT
// ============================================================================

function bootstrap(sessionId: string | undefined) {
  const frk: { 
    f: any[]; 
    CommandQueue?: any[]; 
    eventCount?: number; 
    send?: () => void 
  } = { f: [] };

  // Initialize systems
  initializePredefinedFunctions();
  initializeClassLoading();
  initializeObjectManagement();

  if (window.legacyJsLoad) window.legacyJsLoad();

  // Setup command queue and jsE function
  frk.CommandQueue = [];
  frk.eventCount = 0;
  
  window.jsE = function (objectId: number, data: any, delay?: boolean) {
    if (!frk.CommandQueue) frk.CommandQueue = [];
    frk.CommandQueue.push({ i: parseInt(objectId.toString()), d: data });
    if (frk.send && delay !== true) frk.send();
  };

  // Create SignalR connection
  const thisConnection = new signalR.HubConnectionBuilder()
    .withUrl("/messagehub/msg")
    .configureLogging(signalR.LogLevel.Information)
    .withHubProtocol(new MessagePackHubProtocol())
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (retryContext: { previousRetryCount: number; retryReason: any; }) => {
        console.log(
          `SignalR => Auto reconnecting... Attempt: ${retryContext.previousRetryCount + 1}, Retry reason: ${JSON.stringify(retryContext.retryReason)}`
        );
        return 2000;
      }
    })
    .build();

  thisConnection.serverTimeoutInMilliseconds = 7000;

  // Setup message handling
  let cmdSequence = 1;
  const handleExecPayload = function (payload: [any, any, any][]) {
    window.afterRender = [];
    payload.forEach(function ([f, a, s]) {
      if (s === cmdSequence) {
        applyMessage([f, a, s]);
        cmdSequence++;
      }
    });
    window.afterRender.forEach(fn => { 
      if (typeof fn === "function") fn(); 
    });
    window.afterRender = [];
    if (thisConnection) {
      thisConnection.invoke("AcknowledgeProcessing", cmdSequence);
    }
  };

  // Setup connection event handlers
  thisConnection.onreconnected(function (e: any) {
    thisConnection?.invoke("RegisterSession", sessionId);
    thisConnection?.invoke("ResendUnconfirmedBatches");
    console.log("Upgraded connection with ID " + sessionId);
    console.log("Reconnected to SingleLogic Hub");
    setTimeout(removeOverlay, 500);
  });

  thisConnection.onreconnecting(function (e: any) {
    console.log("Reconnecting to SingleLogic Hub");
    createOverlay();
  });

  thisConnection.onclose(function (e: any) {
    console.log("Disconnected from SingleLogic Hub");
    createOverlay();
  });

  // Register message handlers
  thisConnection.on("exec", handleExecPayload);
  thisConnection.on("sessionwindowreload", function () {
    window.skipExitValidation = true;
    document.body.innerHTML = '<div style="font-family: monospace; font-size:15px; display: flex; justify-content: center; align-items: center; height: 100%;">[ REINICIALIZANDO ]</div>';
    window.setTimeout(function () {
      window.skipExitValidation = true;
      window.location.reload();
    }, 3000);
  });

  // Initialize page objects
  if (typeof window.ood === "function") { 
    window.ood(1, window.document.body); 
  }
  if (typeof window.jsE === "function") {
    window.jsE(0, "screen|" + window.innerWidth, true);
    window.jsE(0, "loaded");
  }

  // Setup send function
  frk.send = function () {
    if (thisConnection == null) return;
    if (thisConnection.state != "Connected") return;
    thisConnection.invoke("ProcessEvent", JSON.stringify(frk.CommandQueue));
    frk.CommandQueue = [];
  };

  // Start connection
  thisConnection
    .start()
    .then(function () {
      console.log("Connected to SingleLogic Hub");
      setTimeout(removeOverlay, 500);
      window.debugsignalr = thisConnection;
      thisConnection?.invoke("RegisterSession", sessionId);
      thisConnection?.invoke("ResendUnconfirmedBatches");
      if (typeof frk.send === "function") frk.send();
    });
}

// ============================================================================
// EVENT LISTENERS & INITIALIZATION
// ============================================================================

// Initialize bootstrap URL and start when page loads
//window.bootstrapSessionIdURL = '<!--url-->';
window.addEventListener('load', function () {
  bootstrap(window.bootstrapSessionIdURL);
});

// Handle cross-window message relay (e.g., TxTextDocument)
window.addEventListener("message", function (event) {
  if (event.data.relay) {
    if (event.data.text && typeof window.jsE === "function") {
      window.jsE(event.data.id, event.data.text);
    }
    if (event.data.payload && typeof window.jsE === "function") {
      window.jsE(event.data.id, event.data.payload);
    }
  }
}, false);

// Setup exit validation
window.skipExitValidation = true;
window.onbeforeunload = function (e) {
  if (typeof window.jsE === "function") {
    window.jsE(0, "dispose");
  }
  window.setTimeout(function () {
    window.jsE!(0, "not-dispose");
  }, 1000);
  if (window.skipExitValidation) return null;
  return "¿Está seguro que desea cerrar la ventana?";
};



